/**
 * Handling of a session token the backend rejects although it is not expired (account disabled,
 * signing key rotated, token revoked or corrupted). The backend answers with `AUTH_NOT_AUTHORIZED`
 * for that, but also for a signed-in user calling a field their role can't use, so a rejection is
 * first checked against `profile` before anyone is signed out.
 */

export const SESSION_ENDED_MESSAGE = 'Your session has ended, please sign in again';

export function isAuthRejected(response: any): boolean {
  const errors: any[] = response?.errors ?? [];
  return errors.some((error) => error?.extensions?.code === 'AUTH_NOT_AUTHORIZED'
    || error?.extensions?.exception?.status === 401
    || error?.message === 'Unauthorized');
}

export function signInPathFor(fullPath: string): string {
  const loginPath = fullPath.startsWith('/web') ? '/auth/web/signin' : '/auth';
  return `${loginPath}?redirect=${encodeURIComponent(fullPath)}`;
}

export interface SessionRecoveryDeps {
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  /** Resolves true when `token` is accepted for the signed-in user's own profile. */
  isTokenAccepted: (token: string) => Promise<boolean>;
  /** Exchanges the refresh token for a new access token, or null. */
  refresh: (refreshToken: string) => Promise<string | null>;
  setToken: (token: string) => void;
  endSession: () => void;
}

/**
 * What to do after a request made with `rejectedToken` was refused:
 * - `{ retryWith: token }`: the session is fine (now), repeat the request with this token;
 * - `{ retryWith: null, sessionValid: true }`: the token is fine, the error is a role restriction;
 * - `{ retryWith: null, sessionValid: false }`: the session was ended.
 */
export type RecoveryResult = { retryWith: string | null; sessionValid: boolean };

export function createSessionRecovery(deps: SessionRecoveryDeps) {
  // Concurrent failures of one page load share a single check/refresh/sign-out.
  const pending = new Map<string, Promise<RecoveryResult>>();

  // Another request may already have refreshed or ended the session.
  function replacedToken(rejectedToken: string): RecoveryResult | null {
    const current = deps.getToken();
    return current === rejectedToken ? null : { retryWith: current, sessionValid: !!current };
  }

  async function recover(rejectedToken: string): Promise<RecoveryResult> {
    const replacedBefore = replacedToken(rejectedToken);
    if (replacedBefore) return replacedBefore;

    if (await deps.isTokenAccepted(rejectedToken)) {
      return { retryWith: null, sessionValid: true };
    }

    const replacedAfter = replacedToken(rejectedToken);
    if (replacedAfter) return replacedAfter;

    const refreshToken = deps.getRefreshToken();
    const newToken = refreshToken ? await deps.refresh(refreshToken).catch(() => null) : null;
    if (newToken && newToken !== rejectedToken && await deps.isTokenAccepted(newToken)) {
      deps.setToken(newToken);
      return { retryWith: newToken, sessionValid: true };
    }

    deps.endSession();
    return { retryWith: null, sessionValid: false };
  }

  return function handleRejectedToken(rejectedToken: string): Promise<RecoveryResult> {
    let promise = pending.get(rejectedToken);
    if (!promise) {
      promise = recover(rejectedToken).finally(() => pending.delete(rejectedToken));
      pending.set(rejectedToken, promise);
    }
    return promise;
  };
}
